<div class="content-container h-full flex flex-col justify-between">
  <div>
    @if (isLoading()) {
      <lib-overlay-loader />
    }

    <header class="py-lg flex items-center">
      <h1 class="text-xl leading-7 font-semibold">
        {{ pageTitle() }}
      </h1>
    </header>

    <form [formGroup]="departmentFormGroup()" libScrollToError>
      <section>
        <h5
          class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 my-2xl">
          {{
            'department_form_screen_department_image_section_title' | transloco
          }}
        </h5>

        <div class="image-section">
          <div>
            <lib-upload-files
              [uploadedFiles]="editMode() ? uploadedImage() : []"
              [id]="'image'"
              [name]="'image'"
              [uploadUrl]="uploadFileUrls.upload"
              [removeUrl]="uploadFileUrls.remove"
              [label]="'group_form_screen_upload_image_label' | transloco"
              [inputFormControlName]="'image'"
              [accept]="'png,jpeg,jpg,webp'"
              [multiple]="false"
              [fileSizeLimit]="2048 * 1000"
              [viewOnly]="false"
              [required]="false"
              [uploadedFileLimit]="1"
              [uploadFileLabels]="{
                placeholder: 'upload_files_placeholder' | transloco,
                singleFileAttachedLabel:
                  'upload_files_single_file_attached_label' | transloco,
                multipleFilesAttachedLabel:
                  'upload_files_multiple_files_attached_label' | transloco,
                addMoreLabel: 'upload_files_add_more_label' | transloco,
                attachmentsTitle: 'upload_files_attachments_title' | transloco,
                fileUploadTitle: 'upload_files_file_upload_title' | transloco,
                dragAndDropLabel:
                  'upload_files_drag_and_drop_label' | transloco,
                orLabel: 'upload_files_or_label' | transloco,
                uploadFromComputerLabel:
                  'upload_files_upload_from_computer_label' | transloco,
                maxSizeLabel: 'upload_files_max_size_label' | transloco,
                perFileLabel: 'upload_files_per_file_label' | transloco,
                maxFilesLimitLabel:
                  'upload_files_max_files_limit_label' | transloco,
                cancelButtonLabel:
                  'upload_files_cancel_button_label' | transloco,
                continueButtonLabel:
                  'upload_files_continue_button_label' | transloco,
                singleFileUploadedLabel:
                  'upload_files_single_file_uploaded_label' | transloco,
                multipleFilesUploadedLabel:
                  'upload_files_multiple_files_uploaded_label' | transloco,
                singleFileLabel: 'upload_files_single_file_label' | transloco,
                multipleFilesLabel:
                  'upload_files_multiple_files_label' | transloco,
                allFormatsAcceptedLabel:
                  'upload_files_all_formats_accepted_label' | transloco,
                acceptedFormatLabel:
                  'upload_files_accepted_format_label' | transloco,
                acceptedFormatsLabel:
                  'upload_files_accepted_formats_label' | transloco,
                uploadedLabel: 'upload_files_uploaded_label' | transloco,
                deleteWarningPopupTitle:
                  'department_form_screen_upload_image_warning_popup_title'
                  | transloco,
                deleteWarningPopupDeclaimerMessage:
                  'upload_image_warning_popup_declaimer_message' | transloco,
                deleteWarningPopupCancelButton:
                  'upload_image_warning_popup_cancel_button' | transloco,
                deleteWarningPopupContinueButton:
                  'upload_image_warning_popup_delete_button' | transloco,
              }" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="image" />
          </div>
        </div>
      </section>

      <section>
        <h5
          class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 my-2xl">
          {{ 'department_form_screen_department_section_title' | transloco }}
        </h5>

        <div class="info-section gap-lg">
          <div>
            <div formGroupName="name">
              <dual-lang-input
                [requiredGroup]="true"
                [inputFormControlName]="'name'"
                [id]="'name'"
                type="text"
                name="name"
                [requiredEn]="false"
                [requiredAr]="false"
                [label]="
                  'department_form_screen_department_name_label' | transloco
                "
                [placeholderEn]="
                  'department_form_screen_department_name_placeholder_en'
                    | transloco
                "
                [placeholderAr]="
                  'department_form_screen_department_name_placeholder_ar'
                    | transloco
                " />
            </div>
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="name" />
          </div>

          <div>
            <lib-input
              [inputFormControlName]="'identifier'"
              [id]="'identifier'"
              type="text"
              name="identifier"
              [label]="'department_form_screen_department_id_label' | transloco"
              [placeholder]="'id_input_default_placeholder' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="identifier" />
          </div>

          <div>
            <lib-select
              [inputFormControlName]="'legal_entity_id'"
              id="legal_entity_id"
              name="legal_entity_id"
              [label]="'department_form_screen_legal_entity_label' | transloco"
              [required]="true"
              [placeholder]="
                'department_form_screen_legal_entity_placeholder' | transloco
              "
              [options]="legalEntity()"
              [selectedKeyValue]="'value'"
              [selectedKeyName]="'label'" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="legal_entity_id" />
          </div>

          <div>
            <lib-paginated-select
              [id]="'parent_id'"
              [inputFormControlName]="'parent_id'"
              [name]="'parent_id'"
              [label]="'department_form_screen_parent_label' | transloco"
              [placeholder]="
                'department_form_screen_parent_placeholder' | transloco
              "
              [classList]="['text-sm']"
              [apiUrl]="parentApi"
              [filters]="parentFilter"
              [selectedOption]="selectedParent"
              [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="parent_id" />
          </div>

          <div>
            <lib-paginated-select
              [id]="'direct_manager_id'"
              [inputFormControlName]="'direct_manager_id'"
              [name]="'direct_manager_id'"
              [label]="
                'department_form_screen_direct_manager_label' | transloco
              "
              [placeholder]="
                'department_form_screen_direct_manager_placeholder' | transloco
              "
              [classList]="['text-sm']"
              [apiUrl]="directManagerApi"
              [selectedOption]="selectedDirectManager"
              [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="direct_manager_id" />
          </div>

          <div>
            <lib-paginated-select
              [id]="'department_type_option_id'"
              [inputFormControlName]="'department_type_option_id'"
              [name]="'department_type_option_id'"
              [label]="
                'department_form_screen_department_type_label' | transloco
              "
              [placeholder]="
                'department_form_screen_department_type_placeholder' | transloco
              "
              [classList]="['text-sm']"
              [apiUrl]="variableOptionsApi"
              [filters]="departmentTypeFilter"
              [selectedOption]="selectedDepartmentType"
              [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="department_type_option_id" />
          </div>
        </div>
      </section>
    </form>
  </div>

  <footer class="flex justify-end items-end pb-2xl">
    <div class="flex justify-end gap-lg mt-4xl">
      <a
        id="cancel"
        class="px-2xl py-sm rounded-md text-neutral-700 bg-white text-lg font-medium cursor-pointer"
        [routerLink]="[
          editMode()
            ? (`${ADMIN_ROUTES.DEPARTMENTS.DETAILS}/${departmentDetails.id}`
              | tenantPath: 'admin')
            : (`${ADMIN_ROUTES.DEPARTMENTS.LIST}` | tenantPath: 'admin'),
        ]">
        {{ 'department_form_screen_cancel_button' | transloco }}
      </a>

      <button
        id="submit"
        class="px-2xl py-sm rounded-md text-white bg-primary-500 text-lg font-medium border-none cursor-pointer"
        (click)="submit()"
        [class.opacity-30]="isLoading()"
        [disabled]="isLoading()">
        {{ buttonText() }}
      </button>
    </div>
  </footer>
</div>
